import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Calendar, Mail, X, Clock } from 'lucide-react';

const AuditorAccess = ({ onSubmit, onNext, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    auditorFirm: initialData.auditorFirm || '',
    leadEmail: initialData.leadEmail || '',
    accessFrom: initialData.accessFrom || '',
    accessTo: initialData.accessTo || '',
    ...initialData
  });

  const [auditors, setAuditors] = useState(initialData.auditors || []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const addAuditor = () => {
    const newAuditor = {
      id: Date.now(),
      firm: formData.auditorFirm,
      email: formData.leadEmail,
      accessFrom: formData.accessFrom,
      accessTo: formData.accessTo,
      status: 'active'
    };
    setAuditors(prev => [...prev, newAuditor]);
    
    // Clear form
    setFormData(prev => ({
      ...prev,
      auditorFirm: '',
      leadEmail: '',
      accessFrom: '',
      accessTo: ''
    }));
  };

  const removeAuditor = (id) => {
    setAuditors(prev => prev.filter(auditor => auditor.id !== id));
  };

  const updateAuditorStatus = (id, status) => {
    setAuditors(prev => prev.map(auditor => 
      auditor.id === id ? { ...auditor, status } : auditor
    ));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {t('workflow.auditQA.auditorAccess.title')}
        </h2>
        
        <p className="text-gray-600 mb-8">
          {t('workflow.auditQA.auditorAccess.description')}
        </p>

        {/* Status Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {auditors.length} {t('workflow.auditQA.auditorAccess.auditorsActive')}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  {t('workflow.auditQA.auditorAccess.accessWindow')}: {formData.accessFrom || t('workflow.auditQA.auditorAccess.notSet')} - {formData.accessTo || t('workflow.auditQA.auditorAccess.notSet')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Add Auditor Form */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.auditorAccess.addAuditor')}
          </h3>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.auditorAccess.auditorFirm')}
                </label>
                <input
                  type="text"
                  value={formData.auditorFirm}
                  onChange={(e) => handleInputChange('auditorFirm', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.auditQA.auditorAccess.auditorFirmPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.auditorAccess.leadEmail')}
                </label>
                <input
                  type="email"
                  value={formData.leadEmail}
                  onChange={(e) => handleInputChange('leadEmail', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.auditQA.auditorAccess.leadEmailPlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.auditorAccess.accessFrom')}
                </label>
                <input
                  type="date"
                  value={formData.accessFrom}
                  onChange={(e) => handleInputChange('accessFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.auditorAccess.accessTo')}
                </label>
                <input
                  type="date"
                  value={formData.accessTo}
                  onChange={(e) => handleInputChange('accessTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={addAuditor}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('workflow.auditQA.auditorAccess.inviteAuditor')}
              </button>
              
              <button
                type="button"
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors flex items-center"
              >
                <Clock className="w-4 h-4 mr-2" />
                {t('workflow.auditQA.auditorAccess.extend')}
              </button>
              
              <button
                type="button"
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors flex items-center"
              >
                <X className="w-4 h-4 mr-2" />
                {t('workflow.auditQA.auditorAccess.revoke')}
              </button>
            </div>
          </div>
        </div>


        {/* Active Auditors List */}
        {auditors.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('workflow.auditQA.auditorAccess.activeAuditors')}
            </h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('workflow.auditQA.auditorAccess.firm')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('workflow.auditQA.auditorAccess.leadEmail')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('workflow.auditQA.auditorAccess.accessPeriod')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('workflow.auditQA.auditorAccess.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('workflow.auditQA.auditorAccess.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {auditors.map((auditor) => (
                    <tr key={auditor.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {auditor.firm}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {auditor.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {auditor.accessFrom} - {auditor.accessTo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          auditor.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {auditor.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => updateAuditorStatus(auditor.id, auditor.status === 'active' ? 'revoked' : 'active')}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {auditor.status === 'active' ? t('workflow.auditQA.auditorAccess.revoke') : t('workflow.auditQA.auditorAccess.activate')}
                          </button>
                          <button
                            onClick={() => removeAuditor(auditor.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

    </div>
  );
};

export default AuditorAccess;
